import { Link, useNavigate, useParams } from 'react-router-dom';
import { isSystemVariable, SystemVariable } from '../../types';
import { PropsWithChildren, useState } from 'react';
import { DateTime } from 'luxon';
import { Icon } from 'components/UI/Icons';
import { Colors, Svgs } from 'environment';
import clsx from 'clsx';
import { SeriesEntryTableColumn, SeriesEntryTableVariables } from './useSeriesTablesDataQuery';
import { Skeleton } from 'features/entry-form-v2/component/Skeleton';
import { createEntriesSort } from './entriesSort';
import { ROUTE_MAP } from 'features/entry-form-v2/utils/routeMap';
import { Button } from 'features/components/Button';
import { BackendEntryValue } from 'features/entry-form-v2/types';

export const SeriesEntryBody = ({
	seriesName,
	columns,
	rows,
	variables,
	projectId,
	entryId
}: {
	seriesName: string;
	columns: SeriesEntryTableColumn[];
	rows: SeriesEntryBodyRow[];
	variables: SeriesEntryTableVariables;
	projectId: string;
	entryId: string;
}) => {
	return (
		<div className="w-full overflow-x-auto">
			{rows.length === 0 && (
				<div className="flex flex-col items-center justify-center h-full">
					<EmptyDatasetIcon className="w-[200px] h-[200px]" />

					<p className="text-base font-semibold">No entries to display</p>

					<p className="text-base mt-6">Get started by creating your first entry</p>

					<Link
						to={ROUTE_MAP.projects.byId.dataset.update.series.bySeriesName.create.createPath(
							{
								projectId,
								entryId,
								seriesName,
								formId: null
							}
						)}
						className="mt-10"
					>
						<Button text="Create series entry" variant="primary" />
					</Link>
				</div>
			)}

			{rows.length > 0 && (
				<SeriesTable
					columns={columns}
					rows={rows}
					seriesName={seriesName}
					variables={variables}
				/>
			)}
		</div>
	);
};

export const TableSkeleton = () => {
	return (
		<div className="min-w-full flex flex-col gap-2">
			{Array.from({ length: 10 }).map((_, i) => (
				<Skeleton key={i} className="w-full h-12 rounded-md" />
			))}
		</div>
	);
};

const DEFAULT_SORT = {
	variableName: 'creationdate',
	direction: 'desc'
} as const;

type GroupColumnVisibilityState = 'expanded' | 'collapsed';
type ColumnVisibilityStateByGroupName = Record<string, GroupColumnVisibilityState>;
type ColumnSort = { variableName: string; direction: SortDirection };
const SeriesTable = ({
	columns,
	rows,
	variables,
	seriesName
}: {
	columns: SeriesEntryTableColumn[];
	rows: SeriesEntryBodyRow[];
	variables: SeriesEntryTableVariables;
	seriesName: string;
}) => {
	const [groupVisibilityState, setGroupVisibilityState] =
		useState<ColumnVisibilityStateByGroupName>(
			columns.reduce((acc, column) => {
				if (column.type !== 'group') {
					return acc;
				}

				acc[column.group.groupName] = 'expanded';
				return acc;
			}, {} as ColumnVisibilityStateByGroupName)
		);

	const [overrideSort, setOverrideSort] = useState<ColumnSort | undefined>();

	return (
		<table className="w-full overflow-auto">
			<thead className="border-2 border-gray-300">
				<GroupHeaderRow
					columns={columns}
					groupVisibilityState={groupVisibilityState}
					onGroupVisibilityStateChanged={updated =>
						setGroupVisibilityState({
							...groupVisibilityState,
							[updated.groupName]: updated.state
						})
					}
				/>

				<VariableNamesHeaderRow
					columns={columns}
					variables={variables}
					groupVisibilityState={groupVisibilityState}
					currentSort={overrideSort}
					onSortChanged={setOverrideSort}
				/>
			</thead>

			<Body
				columns={columns}
				seriesName={seriesName}
				groupVisibilityState={groupVisibilityState}
				rows={rows.sort((a, b) => {
					const sortState = overrideSort ?? DEFAULT_SORT;

					const variable = variables[sortState.variableName];

					const sort = createEntriesSort({
						variable,
						direction: sortState.direction
					});

					return sort(
						a.fieldsByVariableName[sortState.variableName],
						b.fieldsByVariableName[sortState.variableName]
					);
				})}
				variables={variables}
			/>
		</table>
	);
};

const GroupHeaderRow = ({
	columns,
	groupVisibilityState,
	onGroupVisibilityStateChanged: setGroupVisibilityState
}: {
	columns: SeriesEntryTableColumn[];
	groupVisibilityState: ColumnVisibilityStateByGroupName;
	onGroupVisibilityStateChanged: (newState: {
		groupName: string;
		state: GroupColumnVisibilityState;
	}) => void;
}) => {
	return (
		<tr>
			{columns.map(column => {
				if (column.type === 'variable' || column.type === 'system-variable') {
					return <th key={column.variable.variableName} className="p-4" />;
				}

				const isGroupCollapsed =
					groupVisibilityState[column.group.groupName] === 'collapsed';

				return (
					<th
						colSpan={
							isGroupCollapsed ? 1 : column.group.variablesBelongingToGroup.length
						}
						key={column.group.groupName}
						className="border-t-2 border-accent-500 px-4 py-1"
					>
						<button
							type="button"
							className="text-start font-semibold text-sm w-full flex gap-1 items-center text-primary-500 whitespace-nowrap"
							onClick={() =>
								setGroupVisibilityState({
									groupName: column.group.groupName,
									state: isGroupCollapsed ? 'expanded' : 'collapsed'
								})
							}
						>
							<Icon
								svg={Svgs.ChevronDown}
								size={s => s.m}
								className={clsx(isGroupCollapsed && '-rotate-90')}
								propagate
							/>
							{column.group.groupLabel}
						</button>
					</th>
				);
			})}
		</tr>
	);
};

const VariableNamesHeaderRow = ({
	columns,
	variables,
	groupVisibilityState,
	currentSort,
	onSortChanged
}: {
	columns: SeriesEntryTableColumn[];
	variables: SeriesEntryTableVariables;
	groupVisibilityState: ColumnVisibilityStateByGroupName;
	currentSort?: ColumnSort;
	onSortChanged: (columnSort?: ColumnSort) => void;
}) => {
	return (
		<tr>
			{columns.map(column => {
				if (column.type === 'variable' || column.type === 'system-variable') {
					return (
						<ColumnHeader
							key={column.variable.variableName}
							variableLabel={column.variable.variableLabel}
							variableName={column.variable.variableName}
							currentSort={currentSort}
							onSortChanged={onSortChanged}
						/>
					);
				}

				if (groupVisibilityState[column.group.groupName] === 'collapsed') {
					return <th key={column.group.groupName}></th>;
				}

				return column.group.variablesBelongingToGroup.map(variableName => {
					const variable = variables[variableName];
					if (!variable) {
						console.warn(`Variable ${variableName} not found.`);
						return <th key={variableName}></th>;
					}

					return (
						<ColumnHeader
							key={variable.variableName}
							variableLabel={variable.variableLabel}
							variableName={variable.variableName}
							onSortChanged={onSortChanged}
							currentSort={currentSort}
						/>
					);
				});
			})}
		</tr>
	);
};

export type SeriesEntryBodyRowField = {
	label: string;
	value: BackendEntryValue;
};

export type SeriesEntryBodyRow = {
	fieldsByVariableName: Record<string, SeriesEntryBodyRowField>;
	datasetentryid: string;
};

const Body = ({
	rows,
	columns,
	variables,
	groupVisibilityState,
	seriesName
}: {
	rows: SeriesEntryBodyRow[];
	columns: SeriesEntryTableColumn[];
	variables: SeriesEntryTableVariables;
	groupVisibilityState: ColumnVisibilityStateByGroupName;
	seriesName: string;
}) => {
	const params = useParams();
	const navigate = useNavigate();

	const projectId = params['projectId'] as string;
	const entryId = params['entryId'] as string;

	const navigateToUpdateSeriesEntry = (seriesEntryId: string) =>
		navigate(
			ROUTE_MAP.projects.byId.dataset.update.series.bySeriesName.update.createPath({
				projectId,
				entryId,
				seriesName,
				seriesEntryId,
				formId: null
			})
		);

	return (
		<tbody>
			{rows.map((row, index) => {
				return (
					<tr
						onClick={() => navigateToUpdateSeriesEntry(row.datasetentryid)}
						onKeyDown={e => {
							if (e.key === 'Enter' || e.key === ' ') {
								e.preventDefault();
								navigateToUpdateSeriesEntry(row.datasetentryid);
							}
						}}
						aria-label="Update entry"
						tabIndex={0}
						role="button"
						key={row.datasetentryid}
						className={clsx(index % 2 === 0 && 'bg-gray-300')}
						data-testid={`series-entry-body-row-${row.datasetentryid}`}
					>
						{columns.map(column => {
							if (column.type === 'variable') {
								return (
									<Cell
										key={column.variable.variableName}
										data-testid={`series-entry-row-element-${column.variable.variableName}-${row.datasetentryid}`}
									>
										{
											row.fieldsByVariableName[column.variable.variableName]
												.label
										}
									</Cell>
								);
							}

							if (column.type === 'system-variable') {
								return (
									<Cell
										key={column.variable.variableName}
										data-testid={`series-entry-row-element-${column.variable.variableName}-${row.datasetentryid}`}
									>
										{
											row.fieldsByVariableName[column.variable.variableName]
												.label
										}
									</Cell>
								);
							}

							if (groupVisibilityState[column.group.groupName] === 'collapsed') {
								return (
									<td key={column.group.groupName} className="text-center">
										-
									</td>
								);
							}

							return column.group.variablesBelongingToGroup.map(variableName => {
								const variable = variables[variableName];
								if (!variable) {
									console.warn(`Variable ${variableName} not found.`);
									return <td key={variableName}></td>;
								}

								if (isSystemVariable(variable)) {
									console.error(
										new Error(
											'System variable found in group, this should not be possible'
										)
									);

									return (
										<Cell key={variable.variableName}>
											{row.fieldsByVariableName[variable.variableName].label}
										</Cell>
									);
								}

								return (
									<Cell key={variable.variableName}>
										{row.fieldsByVariableName[variable.variableName].label}
									</Cell>
								);
							});
						})}
					</tr>
				);
			})}
		</tbody>
	);
};

export const formatSystemVariableValue = ({
	variable,
	value,
	namesByUserId
}: {
	variable: SystemVariable;
	value: string;
	namesByUserId: Record<string, string>;
}): string => {
	switch (variable.variableName) {
		case 'creationdate':
		case 'lastmodifieddate':
			return DateTime.fromJSDate(new Date(value)).toLocaleString(DateTime.DATETIME_MED);

		case 'ownedbyuser':
		case 'enteredbyuser':
			return namesByUserId[value] || 'Empty';

		case 'datasetentryid':
			return value;

		default:
			console.error('Unknown system variable', { variable });
			return 'Unknown variable type';
	}
};

const Cell = ({
	children,
	...props
}: PropsWithChildren<React.HTMLAttributes<HTMLTableCellElement>>) => {
	return (
		<td className="whitespace-nowrap p-4 text-base" {...props}>
			{children}
		</td>
	);
};

export type SortDirection = 'asc' | 'desc';

const ColumnHeader = ({
	variableLabel,
	variableName,
	currentSort,
	onSortChanged
}: {
	variableName: string;
	variableLabel: string;
	currentSort?: ColumnSort;
	onSortChanged: (columnSort?: ColumnSort) => void;
}) => {
	const updateSort = () => {
		if (!currentSort || currentSort.variableName !== variableName) {
			onSortChanged({
				variableName: variableName,
				direction: 'asc'
			});
			return;
		}

		if (currentSort.direction === 'asc') {
			onSortChanged({
				variableName: variableName,
				direction: 'desc'
			});
			return;
		}

		onSortChanged(undefined);
	};

	return (
		<th
			key={variableName}
			className="font-semibold text-primary-500 whitespace-nowrap p-4 text-sm text-start border border-gray-300"
		>
			<button type="button" className="flex gap-2 items-center" onClick={updateSort}>
				{variableLabel}

				<Icon
					svg={Svgs.ArrowDown}
					className={clsx(
						currentSort?.direction === 'desc' && '-rotate-180',
						currentSort?.variableName !== variableName && 'invisible'
					)}
					propagate
					customSize={2}
					colors={{
						color: Colors.primary.normal
					}}
				/>
			</button>
		</th>
	);
};

const EmptyDatasetIcon = ({ className }: { className: string }) => (
	<svg
		width="320px"
		height="240px"
		viewBox="0 0 320 240"
		version="1.1"
		xmlns="http://www.w3.org/2000/svg"
		className={className}
	>
		<title>NO DATASET</title>
		<g id="STYLEGUIDE_V1" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
			<g id="ILLUSTRATION" transform="translate(-874, -644)">
				<g id="ILLUSTRATIONS" transform="translate(153, 116)">
					<g id="EMPTY-STATE-GRAPHICS" transform="translate(0, 152)">
						<g id="NO-DATASET" transform="translate(721, 328)">
							<g transform="translate(0, 48)">
								<rect id="BOUNDS" x="0" y="0" width="320" height="240"></rect>
								<path
									d="M148.5,199 C169.21,199 186,182.21 186,161.5 C186,140.79 169.21,124 148.5,124 C127.79,124 111,140.79 111,161.5 C111,182.21 127.79,199 148.5,199 Z"
									id="Path"
									fill="#F4F1ED"
									fill-rule="nonzero"
								></path>
								<path
									d="M180.546886,48 C185.369572,48 189.293008,51.9205458 189.293008,56.7390495 L189.293008,65.1951731 C189.293008,66.3658818 188.343407,67.3149982 187.171468,67.3149982 C185.999529,67.3149982 185.049927,66.3658818 185.049927,65.1951731 L185.049927,60.8744043 L108.243081,60.8744043 L108.243081,98.7746155 L185.049927,98.7746155 L185.049927,73.7047162 C185.049927,72.5340075 185.999529,71.5848911 187.171468,71.5848911 C188.343407,71.5848911 189.293008,72.5340075 189.293008,73.7047162 L189.293008,81.9389647 L209.874782,81.9389647 C210.805997,78.5735305 213.896092,76.0944657 217.556457,76.0944657 C221.951723,76.0944657 225.527509,79.6670775 225.527509,84.0587898 C225.527509,87.7164773 223.046438,90.8037906 219.677997,91.7345351 L219.677997,120.54748 C226.436943,120.76879 232.708217,121.576019 237.634152,122.87957 C244.51247,124.700076 248,127.278914 248,130.544293 L248,181.939028 C248,185.204123 244.512187,187.782961 237.633869,189.603467 C231.794258,191.148961 224.066758,192 215.874216,192 C207.681392,192 199.953892,191.148961 194.114563,189.603467 C187.235962,187.782961 183.74815,185.204123 183.74815,181.939028 L183.74815,164.596315 L173.566734,164.596315 C172.394795,164.596315 171.445194,163.647198 171.445194,162.47649 C171.445194,161.305781 172.394795,160.356664 173.566734,160.356664 L183.74815,160.356664 L183.74815,130.544293 C183.74815,127.279197 187.235962,124.700359 194.114563,122.879853 C199.848946,121.362058 207.405591,120.516107 215.434633,120.486147 L215.434633,91.7348177 C212.742539,90.9909005 210.619018,88.8688142 209.874499,86.1788975 L189.292725,86.1788975 L189.292725,102.910253 C189.292725,107.729039 185.36929,111.649302 180.546603,111.649302 L163.306398,111.649302 L163.306398,117.753551 L170.969686,117.753551 C173.777474,117.753551 176.061666,120.035896 176.061666,122.841131 L176.061666,127.624587 C176.061666,128.795296 175.112064,129.744412 173.940125,129.744412 L147.502334,129.744412 L147.502334,154.490685 C147.502334,154.631158 147.487907,154.768523 147.461883,154.901365 C149.993588,155.723857 151.971996,157.779522 152.685116,160.356382 L165.050304,160.356382 C166.222243,160.356382 167.171845,161.305498 167.171845,162.476207 C167.171845,163.646916 166.222243,164.596032 165.050304,164.596032 L152.685116,164.596032 C151.753901,167.961466 148.663807,170.440531 145.003442,170.440531 C140.608175,170.440531 137.032389,166.867919 137.032389,162.476207 C137.032389,158.679176 139.705813,155.496612 143.270284,154.703515 C143.263212,154.633419 143.259535,154.562476 143.259535,154.490402 L143.259535,129.744129 L118.471736,129.744129 C117.299797,129.744129 116.350196,128.795013 116.350196,127.624304 L116.350196,122.840848 C116.350196,120.035613 118.634388,117.753268 121.441893,117.753268 L129.986328,117.753268 L129.986328,111.64902 L112.746122,111.64902 C107.923436,111.64902 104,107.728757 104,102.90997 L104,56.7390495 C104,51.9205458 107.923436,48 112.746122,48 Z M243.757201,170.202828 C242.16152,171.055846 240.120598,171.813612 237.634152,172.471606 C231.794541,174.0171 224.067041,174.868139 215.874499,174.868139 C207.681675,174.868139 199.954175,174.0171 194.114846,172.471606 C191.6284,171.81333 189.587195,171.055563 187.991514,170.202828 L187.991514,181.938745 C187.991514,182.211496 189.244637,183.928271 195.201075,185.504856 C200.696148,186.959339 208.038093,187.760067 215.874499,187.760067 C223.710905,187.760067 231.052567,186.959339 236.54764,185.504856 C242.503795,183.928271 243.757201,182.211213 243.757201,181.938462 Z M243.757201,153.07125 C242.16152,153.924267 240.120598,154.682034 237.634152,155.340028 C231.794541,156.885522 224.067041,157.736561 215.874499,157.736561 C207.681675,157.736561 199.954175,156.885522 194.114846,155.340028 C191.6284,154.681751 189.587195,153.923985 187.991514,153.07125 L187.991514,164.807167 C187.991514,165.079917 189.244637,166.796693 195.201075,168.373278 C200.696148,169.82776 208.038093,170.628489 215.874499,170.628489 C223.710905,170.628489 231.052567,169.82776 236.54764,168.373278 C242.503795,166.796976 243.757201,165.079917 243.757201,164.807167 Z M145.003159,158.751533 C142.947527,158.751533 141.275187,160.42252 141.275187,162.476207 C141.275187,164.529893 142.947527,166.200881 145.003159,166.200881 C147.05879,166.200881 148.730847,164.530176 148.730847,162.476207 C148.730847,160.422238 147.05879,158.751533 145.003159,158.751533 Z M243.757201,135.939671 C242.16152,136.792689 240.120598,137.550456 237.634152,138.208449 C231.794541,139.753943 224.067041,140.604982 215.874499,140.604982 C207.681675,140.604982 199.954175,139.753943 194.114846,138.208449 C191.6284,137.550173 189.587195,136.792406 187.991514,135.939671 L187.991514,147.675588 C187.991514,147.948339 189.244637,149.665115 195.201075,151.241699 C200.696148,152.696182 208.038093,153.496911 215.874499,153.496911 C223.710905,153.496911 231.052567,152.696182 236.54764,151.241699 C242.503795,149.665397 243.757201,147.948339 243.757201,147.675588 Z M215.874499,124.722688 C208.038093,124.722688 200.696148,125.523416 195.201075,126.977899 C189.244637,128.554201 187.991514,130.271259 187.991514,130.54401 C187.991514,130.816761 189.244637,132.533536 195.201075,134.110121 C200.696148,135.564604 208.038093,136.365332 215.874499,136.365332 C223.710905,136.365332 231.052567,135.564604 236.54764,134.110121 C242.503795,132.533819 243.757201,130.816761 243.757201,130.54401 C243.757201,130.271259 242.504078,128.554483 236.54764,126.977899 C231.052567,125.523416 223.710905,124.722688 215.874499,124.722688 Z M170.969968,121.992918 L121.441893,121.992918 C120.981943,121.992918 120.593277,122.38127 120.593277,122.840848 L120.593277,125.504479 L171.818868,125.504479 L171.818868,122.840848 C171.818868,122.38127 171.430201,121.992918 170.969968,121.992918 Z M159.063599,111.64902 L134.229409,111.64902 L134.229409,117.677237 L159.063599,117.677237 L159.063599,111.64902 Z M185.044835,103.014548 L108.248456,103.014548 C108.304748,105.446977 110.29843,107.40937 112.746122,107.40937 L180.546886,107.40937 C182.994578,107.40937 184.988261,105.446977 185.044835,103.014548 Z M137.083023,67.3944209 C141.060487,67.3944209 144.296261,70.6275781 144.296261,74.6018262 L144.296261,83.9273607 C144.296261,87.9016087 141.060487,91.1347659 137.083023,91.1347659 L120.734713,91.1347659 C116.757249,91.1347659 113.521475,87.9016087 113.521475,83.9273607 L113.521475,74.6018262 C113.521475,70.6275781 116.757249,67.3944209 120.734713,67.3944209 Z M174.754514,84.7942278 C175.926453,84.7942278 176.876055,85.7433441 176.876055,86.9140529 C176.876055,88.0847616 175.926453,89.0338779 174.754514,89.0338779 L152.908868,89.0338779 C151.736929,89.0338779 150.787327,88.0847616 150.787327,86.9140529 C150.787327,85.7433441 151.736929,84.7942278 152.908868,84.7942278 Z M217.556457,80.3341158 C215.500825,80.3341158 213.828768,82.0048206 213.828768,84.0587898 C213.828768,86.1124763 215.500825,87.7834638 217.556457,87.7834638 C219.612088,87.7834638 221.284428,86.1124763 221.284428,84.0587898 C221.284428,82.0051033 219.612088,80.3341158 217.556457,80.3341158 Z M137.083023,71.6340711 L120.734713,71.6340711 C119.096884,71.6340711 117.764556,72.9653212 117.764556,74.6018262 L117.764556,83.9273607 C117.764556,85.5638656 119.096884,86.8951158 120.734713,86.8951158 L137.083023,86.8951158 C138.720852,86.8951158 140.05318,85.5638656 140.05318,83.9273607 L140.05318,74.6018262 C140.05318,72.9656039 138.720852,71.6340711 137.083023,71.6340711 Z M174.754514,76.094183 C175.926453,76.094183 176.876055,77.0432994 176.876055,78.2140081 C176.876055,79.3847168 175.926453,80.3338332 174.754514,80.3338332 L152.908868,80.3338332 C151.736929,80.3338332 150.787327,79.3847168 150.787327,78.2140081 C150.787327,77.0432994 151.736929,76.094183 152.908868,76.094183 Z M174.754514,67.3944209 C175.926453,67.3944209 176.876055,68.3435373 176.876055,69.514246 C176.876055,70.6849547 175.926453,71.6340711 174.754514,71.6340711 L152.908868,71.6340711 C151.736929,71.6340711 150.787327,70.6849547 150.787327,69.514246 C150.787327,68.3435373 151.736929,67.3944209 152.908868,67.3944209 Z M180.547169,52.2396502 L112.746122,52.2396502 C110.29843,52.2396502 108.304465,54.2023255 108.248173,56.6347541 L185.044835,56.6347541 C184.988544,54.2023255 182.994861,52.2396502 180.547169,52.2396502 Z"
									id="Combined-Shape"
									fill="#DEDAD5"
									fill-rule="nonzero"
								></path>
							</g>
						</g>
					</g>
				</g>
			</g>
		</g>
	</svg>
);
